// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import findProductsByErps from "@bg/App/Handlers/FindProductByErp/FindProductsByErps";
import { CustomAppState } from "@bg/App/Store/Reducers";
import WidgetTitle from "@bgs/App/Components/WidgetTitle";
import ProductCarouselProduct from "@bgs/Overrides/Components/ProductCarouselProduct";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showImage = "showImage",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    productsErpNumbers = "productsErpNumbers",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImage]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.productsErpNumbers]: any[];
    };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    let erpNumbers = [] as string[];
    if (props.fields.productsErpNumbers.length > 0) {
        erpNumbers = props.fields.productsErpNumbers.map(item => item.fields.erpNumber);
    }

    return {
        isLoading: state.shopByLook.isErpsLoading,
        shopByLookProducts: state.shopByLook.productsByErps,
        erpNumbers,
    };
};

const mapDispatchToProps = {
    findProductsByErps,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ShopByLookProductsStyles {
    shopByLookWrapper?: InjectableCss;
    loadMore?: InjectableCss;
}

export const styles: ShopByLookProductsStyles = {
    shopByLookWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            margin-right: -40px;
            @media (max-width: 1024px) {
                margin-right: -20px;
            }
            @media (max-width: 670px) {
                margin: 0;
                justify-content: center;
            }
            .product-card {
                margin-top: 40px;
                margin-right: 40px;
                width: calc(100% / 4 - 40px);
                @media (max-width: 1250px) {
                    width: calc(100% / 3 - 40px);
                }
                @media (max-width: 1024px) {
                    width: calc(100% / 3 - 20px);
                    margin-right: 20px;
                }
                @media (max-width: 768px) {
                    width: calc(100% / 2 - 20px);
                }
                @media (max-width: 670px) {
                    width: auto;
                    margin-right: 0;
                }
            }
        `,
    },
    loadMore: {
        css: css`
            display: flex;
            width: 100%;
            margin-top: 40px;
            margin-right: 40px;
            align-items: center;
            justify-content: center;
            padding: 5px;
            background: ${getColor("common.ui4")};
            @media (max-width: 1024px) {
                margin-right: 20px;
            }
            @media (max-width: 670px) {
                margin: 0;
            }
        `,
    },
};

const ShopByLookProducts = ({ id, fields, isLoading, findProductsByErps, shopByLookProducts, erpNumbers }: Props) => {
    const [currentIndex, setCurrentIndex] = React.useState(1);
    const dispatch = useDispatch();
    useEffect(() => {
        if (erpNumbers && erpNumbers.length > 0) {
            findProductsByErps({
                id,
                productNumbers: erpNumbers.slice(0, 8),
            });
            setCurrentIndex(erpNumbers.slice(0, 8).length - 1);
        }

        return () => {
            // clear the store to allow for new data coming form another widget authored in another page
            dispatch({
                type: "ShopByLook/resetShopByLook" as any,
            });
        };
    }, []);

    const loadMoreProds = () => {
        if (currentIndex < erpNumbers.length - 1) {
            findProductsByErps({
                id,
                productNumbers: erpNumbers.slice(currentIndex, currentIndex + 8),
            });
        }
        setCurrentIndex(currentIndex + 8);
    };

    if (!shopByLookProducts || !shopByLookProducts.length) {
        return null;
    }

    const { title } = fields;
    return (
        <StyledWrapper>
            <WidgetTitle title={title} />
            <LoadingOverlay loading={isLoading}>
                <StyledWrapper {...styles.shopByLookWrapper}>
                    {shopByLookProducts
                        .filter((product: any) => erpNumbers.includes(product.productNumber))
                        .map((product: any, index: number) => (
                            <ProductCarouselProduct
                                fromShopByLook={true}
                                key={`gridDisplayShopByLookProduct_${product.id + index}`}
                                carouselId={id}
                                product={product}
                                showImage={fields.showImage}
                                showPartNumbers={fields.showPartNumbers}
                                showPrice={fields.showPrice}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList}
                                hideAvailability={true}
                            />
                        ))}
                    {currentIndex + 1 < erpNumbers.length && shopByLookProducts.length < erpNumbers.length && (
                        <Link {...styles.loadMore} onClick={loadMoreProds}>
                            {!isLoading && translate("Load More")}
                            {isLoading && <LoadingSpinner size={18} />}
                        </Link>
                    )}
                </StyledWrapper>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    icon: "Carousel",
    displayName: "ShopByLook Products",
    allowedContexts: ["ShopByLookPage" as any],
    fieldDefinitions: [
        {
            displayName: "Section Title",
            name: fields.title,
            editorTemplate: "TextField",
            defaultValue: "Get The Look",
            fieldType: "Translatable",
        },
        {
            name: fields.showImage,
            displayName: "Image",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            isEnabled: () => false,
            fieldType: "General",
            sortOrder: 8,
        },
        {
            name: fields.showPartNumbers,
            displayName: "Part Numbers",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 11,
            //  isVisible: () => BLUEPRINT_NAME !== "bgs",
        },
        {
            name: fields.showPrice,
            displayName: "Price",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 12,
        },
        {
            name: fields.showAddToCart,
            displayName: "Add to Cart",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 13,
        },
        {
            name: fields.showAddToList,
            displayName: "Add to List",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 14,
        },
        {
            name: fields.productsErpNumbers,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields) => {
                return item.fields.erpNumber;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "erpNumber",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                    displayName: "Erp Number",
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByLookProducts),
    definition,
};

export default widgetModule;
